/**
 * Univer Setup - Preset Mode
 * Using @univerjs/preset-sheets-core for built-in Excel-like ribbon
 * Using @univerjs/preset-sheets-thread-comment for cell comments/notes
 * https://docs.univer.ai/guides/sheets/features/core
 */

import { createUniver as createUniverPresetInstance, LocaleType, mergeLocales } from '@univerjs/presets';
import { UniverSheetsCorePreset } from '@univerjs/preset-sheets-core';

// @ts-ignore - No type declarations for this preset
// import { UniverSheetsThreadCommentPreset } from '@univerjs/preset-sheets-thread-comment';

// Import Preset CSS
import '@univerjs/preset-sheets-core/lib/index.css';
// import '@univerjs/preset-sheets-thread-comment/lib/index.css';

// Import locale with require (TypeScript can't find declaration for locales)
// eslint-disable-next-line @typescript-eslint/no-var-requires, import/first
const UniverPresetSheetsCoreEnUS = require('@univerjs/preset-sheets-core/lib/locales/en-US');
// eslint-disable-next-line @typescript-eslint/no-var-requires
// const UniverPresetSheetsThreadCommentEnUS = require('@univerjs/preset-sheets-thread-comment/lib/locales/en-US');

let univerInstance: any = null;
let univerAPI: any = null;

export function createUniver(container: string | HTMLElement) {
  console.log('🔧 [DEBUG] Creating Univer instance with Preset Mode...');
  
  const result = createUniverPresetInstance({
    locale: LocaleType.EN_US,
    locales: {
      [LocaleType.EN_US]: mergeLocales(
        UniverPresetSheetsCoreEnUS,
        // UniverPresetSheetsThreadCommentEnUS,
      ),
    },
    presets: [
      UniverSheetsCorePreset({
        container: typeof container === 'string' ? container : container,
        header: true,
        toolbar: true,
        ribbonType: 'default',
        footer: {
          sheetBar: true,
          statisticBar: true,
          menus: true,
          zoomSlider: true,
        },
        contextMenu: true,
      }),
      // UniverSheetsThreadCommentPreset(),
    ],
  });
  
  const univer = result.univer;
  const api = result.univerAPI;
  
  univerInstance = univer;
  univerAPI = api;

  console.log('✅ Univer Preset Mode initialized with Excel-like ribbon');
  console.log('💡 Features: Toolbar, Formatting, Formulas, Sheet tabs, Editing, Comments/Notes');
  console.log('🔧 [DEBUG] Using Core preset + Thread Comment preset');

  // Expose for debugging
  (window as any).__univer__ = univer;
  (window as any).__univerAPI__ = api;

  return { univer, univerAPI: api };
}

export async function createWorkbookWithData(univerInstanceOrData: any, workbookData?: any) {
  // Support both old API (univer, workbookData) and new API (workbookData only)
  const data = workbookData || univerInstanceOrData;
  const api = univerAPI;
  
  if (!api) {
    throw new Error('Univer not initialized. Call createUniver first.');
  }

  console.log('🔧 [DEBUG] Creating workbook with data...');
  console.log('🔧 [DEBUG] Workbook data keys:', Object.keys(data));
  console.log('🔧 [DEBUG] Sheet IDs:', data.sheetOrder);
  
  // Verify cell data structure
  if (data.sheets) {
    Object.keys(data.sheets).forEach(sheetId => {
      const sheet = data.sheets[sheetId];
      const cellCount = sheet.cellData ? Object.keys(sheet.cellData).length : 0;
      console.log(`🔧 [DEBUG] Sheet ${sheetId}: ${cellCount} rows with data`);
      
      // Log first few cells for debugging
      if (sheet.cellData && sheet.cellData[0]) {
        console.log(`🔧 [DEBUG] First row cells:`, sheet.cellData[0]);
      }
    });
  }
  
  // Create workbook using Facade API
  console.log('🔧 [DEBUG] Creating workbook via univerAPI.createWorkbook()...');
  const workbook = api.createWorkbook(data);
  console.log('🔧 [DEBUG] Workbook created:', workbook?.getId?.());
  
  // Wait a moment for UI to initialize
  await new Promise(resolve => setTimeout(resolve, 100));
  
  console.log('✅ Workbook initialized successfully');
  console.log('🎯 Try using the toolbar buttons to format cells, add filters, etc!');
  
  return workbook;
}

// Export types for compatibility
export { LocaleType };
export const UniverInstanceType = {
  UNIVER_SHEET: 2,
  UNIVER_DOC: 1,
  UNIVER_SLIDE: 3,
};
